import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { firestoreService } from '../../services/firestoreService';
import AppIcon from '../ui/AppIcon';
import {
  FaMusic,
  FaVideo,
  FaOm,
  FaStar,
  FaRegStar,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaLock,
  FaWandMagicSparkles,
  FaCircleInfo
} from 'react-icons/fa6';

const sampleMedia = [
  {
    id: 'sample-1',
    title: 'Inner Peace Meditation',
    artist: 'NIL Music',
    type: 'song',
    duration: '4:32',
    lyrics: 'Find your center, breathe in light...\nLet the world fade, hold on tight...',
    url: null,
  },
  {
    id: 'sample-2',
    title: 'Mindful Breathing Guide',
    artist: 'NIL Wellness',
    type: 'video',
    duration: '8:15',
    url: null,
  },
  {
    id: 'sample-3',
    title: 'Body Scan Meditation',
    artist: 'Maya Chen',
    type: 'meditation',
    format: 'audio',
    duration: '10:00',
    description: 'A gentle guided body scan to release tension and settle the mind before sleep.',
    url: null,
  },
];

const CATEGORIES = [
  { key: 'songs', label: 'Songs', icon: FaMusic, type: 'song' },
  { key: 'videos', label: 'Videos', icon: FaVideo, type: 'video' },
  { key: 'meditations', label: 'Meditations', icon: FaOm, type: 'meditation' },
];

const TYPE_LABELS = { song: 'Song', video: 'Video', meditation: 'Meditation' };

const getPlaybackKind = (item) => {
  if (item.type === 'video') return 'video';
  if (item.type === 'meditation' && item.format === 'video') return 'video';
  return 'audio';
};

const StarRating = ({ rating, onRate }) => (
  <div className="star-rating">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        className="star-button"
        onClick={() => onRate(star)}
        aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
      >
        {star <= rating ? <FaStar /> : <FaRegStar />}
      </button>
    ))}
  </div>
);

const MediaCard = ({ item, isExpanded, onToggleExpand, rating, onRate, isLoggedIn, onRequireLogin }) => {
  const kind = getPlaybackKind(item);
  const badgeLabel = item.subtype
    || (item.type === 'meditation' ? `Meditation • ${item.format === 'video' ? 'Video' : 'Audio'}` : TYPE_LABELS[item.type]);

  return (
    <div className="media-card">
      <div className="media-card-header" onClick={onToggleExpand}>
        <div className={`media-card-icon media-card-icon-${item.type}`}>
          {item.type === 'song' && <FaMusic />}
          {item.type === 'video' && <FaVideo />}
          {item.type === 'meditation' && <FaOm />}
        </div>
        <div className="media-card-info">
          <h3>{item.title}</h3>
          <p className="media-card-artist">{item.artist}</p>
          <div className="media-card-meta">
            <span className="media-type-badge">{badgeLabel}</span>
            <span className="media-duration"><FaClock size={11} /> {item.duration}</span>
          </div>
        </div>
        <button
          type="button"
          className="expand-toggle"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </div>

      <div className="media-rating-row">
        <span className="rating-label">Rate:</span>
        <StarRating rating={rating} onRate={onRate} />
      </div>

      {isExpanded && (
        <div className="media-expanded">
          {item.url ? (
            kind === 'video' ? (
              <video controls className="media-player" src={item.url} />
            ) : (
              <audio controls className="media-audio-player" src={item.url} />
            )
          ) : (
            <div className="media-coming-soon">
              <FaCircleInfo /> Playback for "{item.title}" is coming soon.
            </div>
          )}

          {item.type === 'song' && (
            item.lyrics ? (
              isLoggedIn ? (
                <div className="lyrics-box">
                  <h4>Lyrics</h4>
                  <p>{item.lyrics}</p>
                </div>
              ) : (
                <button className="btn btn-outline btn-small" onClick={onRequireLogin}>
                  <FaLock size={12} /> Login to view lyrics
                </button>
              )
            ) : (
              <p className="no-lyrics">No lyrics available</p>
            )
          )}

          {item.description && <p className="media-description">{item.description}</p>}
        </div>
      )}
    </div>
  );
};

const MediaPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [ratings, setRatings] = useState({});

  const isLoggedIn = !!user;
  const activeCategory = CATEGORIES.some((c) => c.key === searchParams.get('category'))
    ? searchParams.get('category')
    : 'songs';

  useEffect(() => {
    const fetchMedia = async () => {
      setLoading(true);
      const result = await firestoreService.getMedia();
      if (result.success && result.media.length > 0) {
        setMedia(result.media);
      } else {
        setMedia(sampleMedia);
      }
      setLoading(false);
    };
    fetchMedia();
  }, []);

  const activeCategoryConfig = CATEGORIES.find((c) => c.key === activeCategory) || CATEGORIES[0];
  const filteredMedia = useMemo(
    () => media.filter((item) => item.type === activeCategoryConfig.type),
    [media, activeCategoryConfig]
  );

  const handleSelectCategory = (key) => {
    setSearchParams({ category: key });
  };

  const handleCreate = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    navigate('/create-song');
  };

  const handleToggleExpand = (item) => {
    const willExpand = expandedId !== item.id;
    setExpandedId(willExpand ? item.id : null);
    if (willExpand) {
      firestoreService.incrementMediaPlayCount(item.id);
    }
  };

  const handleRate = async (mediaId, star) => {
    setRatings((prev) => ({ ...prev, [mediaId]: star }));
    await firestoreService.rateMedia(mediaId, star);
  };

  return (
    <div className="media-page">
      <div className="container">
        <div className="media-header">
          <AppIcon size={60} />
          <h1>Media</h1>
          {!isLoggedIn && (
            <div className="guest-banner">
              Guest mode — <Link to="/login">Log in</Link> to access lyrics and more.
            </div>
          )}
        </div>

        <div className="media-category-row">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.key;
            return (
              <button
                key={cat.key}
                type="button"
                className={`media-category-button${isActive ? ' active' : ''}`}
                onClick={() => handleSelectCategory(cat.key)}
                aria-pressed={isActive}
                aria-label={cat.label}
              >
                <Icon /> <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        <button type="button" className="media-create-button" onClick={handleCreate}>
          <span className="media-create-icon"><FaWandMagicSparkles /></span>
          <span className="media-create-text">Create a Personalized Song</span>
        </button>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            Loading media...
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="empty-state">
            <p>No {activeCategoryConfig.label.toLowerCase()} available yet.</p>
          </div>
        ) : (
          <div className="media-list">
            {filteredMedia.map((item) => (
              <MediaCard
                key={item.id}
                item={item}
                isExpanded={expandedId === item.id}
                onToggleExpand={() => handleToggleExpand(item)}
                rating={ratings[item.id] || 0}
                onRate={(star) => handleRate(item.id, star)}
                isLoggedIn={isLoggedIn}
                onRequireLogin={() => navigate('/login')}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaPage;
